import { addMinutesToTimeString, areDatesEqual, dateWithoutTime } from ".";
import { ActivityCarRental, ActivityCarRentalForOutput, ActivityOvernight, ActivityWithTime, Overnight, TripDay } from "../../../models";




/**
 * This function aligns the received days array with the corresponding overnights.
 * Adding a summary of the overnight stay to each trip day.
 * 
 * @param overnights - The list of overnight stays
 * @param days - The list of trip days
 *
 * @returns an array of trip days aligned with their corresponding overnights
 */
export function alignDaysWithOvernights(overnights: Overnight[], days: TripDay[]): TripDay[] {
  return days.map(day => {
    const dayDate = dateWithoutTime(day.date);

    // Find the overnight whose period includes this day (checkOut - 1 day)
    const matchingOvernight = overnights.find(overnight => {
      const checkIn = dateWithoutTime(overnight.checkIn);
      // Make a copy of checkOut before modifying
      const originalCheckOut = dateWithoutTime(overnight.checkOut);
      const checkOut = originalCheckOut ? new Date(originalCheckOut.getTime()) : null;
      if (checkOut) checkOut.setDate(checkOut.getDate() - 1); // reduce by 1 day

      // return the overnight if dayDate is within the checkIn and checkOut range
      return (
        dayDate &&
        checkIn &&
        checkOut &&
        dayDate >= checkIn &&
        dayDate <= checkOut
      );
    });

    // adding the overnightSummary to the day
    if (matchingOvernight) {
      return {
        ...day,
        overnightSummary: matchingOvernight.activityOvernight.overnightSummary
      };
    }
    // Remove activityOvernight if no overnight matches
    const { overnightSummary: activityOvernight, ...rest } = day;
    return rest as TripDay;
  });
}

/**
 * This function realigns the dates of the trip days getting the trip start date.
 * It is used to maintain aligned the date property of each day in its position in the list:
 * E.g. day 1 = 20/08/2025 - day 2 = 21/08/2025
 * 
 * @param startDate - The start date of the trip
 * @param days - The list of trip days
 * 
 * @returns an array of trip days with their dates realigned
 * 
 */
export function alignDaysDates(startDate: Date, days: TripDay[]): TripDay[] {

  // This method has the function to re-align accordingly the dates related 
  // to the days in all this situation:
  // 1- After a drag and drop of a day /
  // 2- After a startDate changed input event
  // 3- After a clone day operation
  // 4- After a remove day operation 
  if (!days || days.length === 0 || !startDate) return [];

  const baseDate = dateWithoutTime(startDate)!;

  const alignedDays: TripDay[] = days.map((day, index) => {
    const updatedDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate() + index,
      0, 0, 0
    );

    return {
      ...day,
      date: updatedDate
    };
  })

  return alignedDays
}

/**
 * Check if every day in the given range is covered by at least one overnight stay.
 *
 * @param overnights - The list of overnight stays
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 *
 * @returns true if every day is covered, false otherwise
 */
export function isEveryDayCoveredByOvernight(
  overnights: Overnight[],
  startDate: Date,
  endDate: Date
): boolean {

  // Build array of all the nights in the range. The last day is only a final
  // check-out day (no night to cover), so it is excluded with `current < last`.
  // Clone the inputs: dateWithoutTime mutates the Date it receives and the loop
  // below advances `current`, so without cloning we would mutate the caller's startDate.
  const days: Date[] = [];
  let current = dateWithoutTime(new Date(startDate))!;
  const last = dateWithoutTime(new Date(endDate))!;
  while (current < last) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  // For each night, check if it is covered by any overnight. An overnight covers
  // the nights in the range [checkIn, checkOut - 1], i.e. check-out is excluded —
  // consistent with alignDaysWithOvernights and the blocked check-in/out helpers.
  for (const day of days) {
    const covered = overnights.some(overnight => {
      const checkIn = dateWithoutTime(new Date(overnight.checkIn))!;
      const checkOut = dateWithoutTime(new Date(overnight.checkOut))!;
      return day >= checkIn && day < checkOut;
    });
    if (!covered) return false;
  }
  return true;
}


/**
 * Tells if a night of the trip is spent in one of the overnights: the day is covered when its date
 * is within [checkIn, checkOut) of an overnight (the check-out day is not a night, the same
 * semantic of alignDaysWithOvernights and isEveryDayCoveredByOvernight).
 *
 * @param overnights - The list of overnights (it is not modified)
 * @param date - The date of the day to check (it is not modified)
 * @returns true if the day is covered by an overnight
 */
export function isDayCoveredByAnOvernight(overnights: Overnight[], date: Date | string): boolean {
  const day = dateWithoutTime(date instanceof Date ? new Date(date.getTime()) : date);
  if (!day || !overnights) return false;

  return overnights.some(overnight => {
    const checkIn = dateWithoutTime(overnight.checkIn instanceof Date ? new Date(overnight.checkIn.getTime()) : overnight.checkIn as string);
    const checkOut = dateWithoutTime(overnight.checkOut instanceof Date ? new Date(overnight.checkOut.getTime()) : overnight.checkOut as string);
    return !!checkIn && !!checkOut && day >= checkIn && day < checkOut;
  });
}

interface OvernightPeriod {
  checkIn: Date | string;
  checkOut: Date | string;
}

const dayOf = (value: Date | string): Date | null =>
  dateWithoutTime(value instanceof Date ? new Date(value.getTime()) : value);

/**
 * Latest check-out selectable for a new/edited overnight that starts on the given check-in: the check-out
 * cannot go beyond the check-in of the first other overnight that starts on or after that check-in,
 * otherwise the new stay would cross it. The single nights already booked are blocked by
 * getDateToDisableForCheckinFromOvernights / getDateToDisableForCheckoutFromOvernights, this limit
 * disables the dates "beyond" another overnight.
 *
 * @param others - the other overnights of the trip (the one being edited excluded)
 * @param checkIn - the selected check-in
 * @returns the latest selectable check-out, or null if no other overnight limits it
 */
export function getMaxCheckoutBeforeNextOvernight(others: OvernightPeriod[], checkIn: Date | string | null | undefined): Date | null {
  const start = checkIn ? dayOf(checkIn) : null;
  if (!start || !others) return null;

  let limit: Date | null = null;
  for (const other of others) {
    const otherCheckIn = dayOf(other.checkIn);
    if (otherCheckIn && otherCheckIn >= start && (!limit || otherCheckIn < limit)) {
      limit = otherCheckIn;
    }
  }
  return limit;
}

/**
 * Earliest check-in selectable for an overnight that ends on the given check-out: the check-in cannot be
 * before the check-out of the other overnights that start before that check-out, otherwise the stay would
 * cross (or enclose) them.
 *
 * @param others - the other overnights of the trip (the one being edited excluded)
 * @param checkOut - the selected check-out
 * @returns the earliest selectable check-in, or null if no other overnight limits it
 */
export function getMinCheckinAfterPreviousOvernights(others: OvernightPeriod[], checkOut: Date | string | null | undefined): Date | null {
  const end = checkOut ? dayOf(checkOut) : null;
  if (!end || !others) return null;

  let limit: Date | null = null;
  for (const other of others) {
    const otherCheckIn = dayOf(other.checkIn);
    const otherCheckOut = dayOf(other.checkOut);
    if (otherCheckIn && otherCheckOut && otherCheckIn < end && (!limit || otherCheckOut > limit)) {
      limit = otherCheckOut;
    }
  }
  return limit;
}

/**
 * Creates an array of all dates within the ranges of checkIn and checkOut - 1 for each overnight.
 *
 * @param overnights - The list of overnights
 * @returns An array of Date objects representing all the dates covered by the overnights
 */
export function getDateToDisableForCheckinFromOvernights(overnights: Overnight[]): Date[] {
  const dates: Date[] = [];

  overnights.forEach(overnight => {
    const checkIn = new Date(overnight.checkIn);
    const checkOut = new Date(overnight.checkOut);

    // Reduce checkOut by 1 day to exclude the last day
    const lastDay = new Date(checkOut);
    lastDay.setDate(lastDay.getDate() - 1);

    // Add all dates from checkIn to lastDay
    for (let current = new Date(checkIn); current <= lastDay; current.setDate(current.getDate() + 1)) {
      dates.push(new Date(current)); // Push a copy of the current date
    }
  });

  return dates;
}


/**
 * Creates an array of the dates that cannot be selected as check-out because the night before is already
 * spent in an overnight: for each overnight the dates from checkIn + 1 to checkOut (both included).
 *
 * @param overnights - The list of overnights
 * @returns An array of Date objects that cannot be used as check-out
 */
export function getDateToDisableForCheckoutFromOvernights(overnights: Overnight[]): Date[] {
  const dates: Date[] = [];

  overnights.forEach(overnight => {
    // A check-out on day D means that the last night of the stay is D - 1. So the check-out dates that
    // cannot be selected are the ones whose previous night is already spent in this overnight:
    // from the day after its check-in up to its check-out (included, because the night before it is taken).
    // The check-in day itself can be selected for the check-out (the previous night is free).
    const checkIn = new Date(overnight.checkIn);
    const checkOut = new Date(overnight.checkOut);

    const checkInDatePlusOne = new Date(checkIn);
    checkInDatePlusOne.setDate(checkInDatePlusOne.getDate() + 1);

    for (let current = checkInDatePlusOne; current <= checkOut; current.setDate(current.getDate() + 1)) {
      dates.push(new Date(current)); // Push a copy of the current date
    }
  });

  return dates;
}


export function getTotalRooms(activityOvernight: ActivityOvernight): number {

  let totalRooms = 0;
  for (const room of activityOvernight.rooms) {
    totalRooms += room.numberOfRooms;
  }
  return totalRooms;
}

/**
 * Calculated the total price of all the activities of every day of trip.
 * 
 * @param days - The trip days 
 * @returns the total price of the activities within the days of the trip
 */
export function getTotalPriceFromDays(days: TripDay[]): number {
  if (!days || days.length === 0) return 0;

  let totalPrice = 0;
  for (const day of days) {
    if (day.activities && day.activities.length > 0) {
      totalPrice += getTotalPriceFromActivities(day.activities);
    }
  }
  return totalPrice;
}

/**
 * Calculates the total price of all the activities of a day.
 *
 * @param activities - The list of activities it can be Activity or ActivityFlat, because both has the totalPrice
 * @returns the total price of the activities
 */
export function getTotalPriceFromActivities(activities: ActivityWithTime[]): number {
  if (!activities || activities.length === 0) return 0;

  let totalPrice = 0;
  for (const activity of activities) {
    if (activity.totalPrice) {
      totalPrice += activity.totalPrice;
    }
  }
  return totalPrice;
}

/**
 * Calculate the total price of all the overnights of the trip.
 * 
 * @param overnights - the overnights of the trip
 * @returns the total price of the overnights
 */
export function getTotalPriceFromOvernights(overnights: Overnight[]): number {
  if (!overnights || overnights.length === 0) return 0;

  let totalPrice = 0;
  for (const overnight of overnights) {
    if (overnight.activityOvernight.totalPrice) {
      totalPrice += overnight.activityOvernight.totalPrice;
    }
  }
  return totalPrice;
}

/**
 * This type is related to the functions that are using a pricing.
 */
export type PricingFn = (values: any) => number;

/**
 * Obtain a unique correlation ID for a trip day. It extract the time value from the date.
 * This ID can be used to correlate actions or data related to that specific day.
 * 
 * @param day - The trip day for which to get the correlation ID
 * @returns a unique correlation ID based on the day's date
 */
export function getDayCorrelationId(day: TripDay): number {
  return day.date ? dateWithoutTime(day.date)!.getTime() : 0;
}

/**
 * This method is invoked only for the trip output generation. It process the days and changes them.
 * It rearranges the activities of the days taking in consideration the car rentals that should span among multiple days.
 * In fact, the logic is to duplicate a car rental in order to add the related drop-off activity in the correct day.
 * 
 * @param days TripDay[] - this array will be modified
 */
export function rearrangeDaysSpanningCarRentals(days: TripDay[]): void {
  // Deep clone the days array to ensure everything is mutable
  // This avoids "TypeError: Cannot add property, object is not extensible" when pushing to activities

  for (const day of days) {
    const processedActivities: ActivityWithTime[] = [];

    for (const activity of day.activities) {
      if (activity.type === 'car-rental') {
        const carRental = activity as ActivityCarRental;

        // Use local copies for date comparison to avoid mutation issues
        const checkInDate = dateWithoutTime(new Date(carRental.checkIn));
        const dayDate = dateWithoutTime(new Date(day.date));

        // Check if this car rental starts on this day (Pick-up)
        if (areDatesEqual(checkInDate, dayDate)) {

          // 1. Create a NEW pick-up activity for the output it update the end time adding an hour
          const carRentalPickupActivity: ActivityCarRentalForOutput = {
            ...carRental,
            name: 'Pick-up',
            startTime: carRental.startTime,
            endTime: addMinutesToTimeString(carRental.startTime, 60),
            isPickup: true,
            isDropOff: false
          };
          processedActivities.push(carRentalPickupActivity);

          // 2. Create a NEW drop-off activity for the output and update the start time with 00:00
          const carRentalDropOffActivity: ActivityCarRentalForOutput = {
            ...JSON.parse(JSON.stringify(carRental)),
            name: 'Drop-off',
            startTime: '00:00',
            isPickup: false,
            isDropOff: true
          };

          const checkOutDate = dateWithoutTime(new Date(carRental.checkOut));

          // If the drop-off is on the same day, add it directly to processedActivities
          if (areDatesEqual(checkOutDate, dayDate)) {
            processedActivities.push(carRentalDropOffActivity);
          } else {
            // Otherwise, add it to the correct future day in our mutable array
            addCarRentalDropOffActivity(days, carRentalDropOffActivity);
          }

          // Skip adding the original unflagged activity
          continue;
        }
      }
      // If it's not a car rental or it's a car rental but not starting today, keep it as is
      processedActivities.push(activity);
    }

    day.activities = processedActivities;
  }
}

/**
 * Adds a car rental drop-off activity to the appropriate trip day.
 * It also checks if the activity is already present to avoid duplicates if the pick-up and drop-off are the same day.
 * @param days TripDay[]
 * @param carRentalDropOffActivity 
 * @returns 
 */
export function addCarRentalDropOffActivity(days: TripDay[], carRentalDropOffActivity: ActivityCarRentalForOutput): TripDay[] {
  const dropOffDate = dateWithoutTime(carRentalDropOffActivity.checkOut);

  for (const day of days) {
    if (areDatesEqual(dropOffDate, dateWithoutTime(day.date))) {
      // Avoid adding the same drop-off activity twice if it was already processed
      // (e.g. if the user called this function multiple times or for same-day rentals)
      const alreadyPresent = day.activities.some(a =>
        a.type === 'car-rental' &&
        (a as unknown as ActivityCarRentalForOutput).isDropOff &&
        a.endTime === carRentalDropOffActivity.endTime &&
        a.name === carRentalDropOffActivity.name
      );

      if (!alreadyPresent) {
        day.activities.push(carRentalDropOffActivity);
      }
    }
  }
  return days;
}

